<script lang="ts" setup>
import InvestorHero from '@/domains/investor/components/Hero/Hero.vue'
import Infoline from '@/domains/about/company/components/Infoline.vue'
import ContactBox from '@/domains/contact/components/ContactBox/ContactBox.vue'
import ContactFaq from '@/domains/contact/components/ContactFaq/ContactFaq.vue'
import { COMPANY } from '@/domains/about/company/const/company.const'
import { useContactPage } from '@/domains/contact/composables/useContactPage'

const { t, locale } = useI18n()
const { topics, faqItems } = useContactPage()

const infolineItems = [
  {
    text: 'infoLine.phone[0].text',
    value: 'contact.help.phone',
  },
  ...COMPANY.infoline.email,
]

useHead(() => ({
  title: t('pageTitles.contact.title'),
  meta: [
    {
      name: 'description',
      content: t('pageTitles.contact.meta'),
    },
  ],
  link: [
    {
      hid: 'favicon',
      rel: 'icon',
      type: 'image/x-icon',
      href: '/favicon.ico',
    },
    {
      rel: 'canonical',
      href: t(`pageTitles.contact.canonical.${locale.value}`),
    },
    {
      rel: 'alternate',
      hreflang: 'pl',
      href: t('pageTitles.contact.canonical.pl'),
    },
    {
      rel: 'alternate',
      hreflang: 'en',
      href: t('pageTitles.contact.canonical.en'),
    },
    {
      rel: 'alternate',
      hreflang: 'x-default',
      href: t('pageTitles.contact.canonical.pl'),
    },
  ],
}))
</script>

<template>
  <div class="contact">
    <InvestorHero
      :title="$t('contact.help.header')"
      image="/images/contact/hero.png"
    />

    <section class="section-investor">
      <div class="contact__help section-investor__container">
        <div>
          <UiHeader
            :header="$t('contact.help.topics.header')"
            :subheader="$t('contact.help.topics.text')"
            position="left"
            header-size="h5"
            header-size-tablet="h5"
            header-size-mobile="h5"
            subheader-size="h9"
            subheader-size-tablet="h9"
            subheader-size-mobile="h9"
          />

          <Infoline
            class="contact__infoline"
            :items="infolineItems"
            hide-graphic
          />
        </div>

        <div class="contact__boxes">
          <ContactBox
            v-for="topic in topics"
            :key="topic.title"
            v-bind="topic"
          />
        </div>
      </div>
    </section>

    <ContactFaq
      :header="$t('contact.help.faq.header')"
      :subheader="$t('contact.help.faq.subheader')"
      :items="faqItems"
    />
  </div>
</template>

<style lang="scss" scoped>
.contact {
  &__help {
    display: flex;
    flex-direction: column;
    gap: 1.6rem;

    :deep(.ui-header__subheader) {
      color: $text-300;
    }

    @include atM {
      gap: 3.2rem;
    }

    @include atXL {
      display: grid;
      grid-template-columns: 1fr 2fr;
      gap: 9.6rem;
    }
  }

  &__infoline {
    margin-top: 4.8rem;

    @include atM {
      margin-top: 6.4rem;
    }
  }

  &__boxes {
    display: grid;
    gap: 1.6rem;

    @include atXS {
      grid-template-columns: repeat(2, 1fr);
    }

    @include atM {
      gap: 2.4rem;
    }

    @include atL {
      grid-template-columns: repeat(4, 1fr);
    }

    @include atXL {
      grid-template-columns: repeat(2, 1fr);
    }
  }
}
</style>
